/**
 * `InstallKeysSection` — ADR-0231 Settings → Install → Install keys (rendered in the Install tab).
 *
 * Lists the site's Cloudflare Worker / WordPress plugin installs and the
 * keys they sign their reports to Sill with (metadata only — no secret
 * is ever shown here). Owner|admin can revoke a key or a whole install.
 *
 * Rendered only for owner|admin by the caller (the server is
 * authoritative; this is defense-in-depth like every other control on
 * the page).
 */

import { KeyRound, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { SiteId } from '@sill/types';
import type {
  InstallKeyView,
  InstallView,
  SiteAPI,
  SiteInstallsResponse,
} from '@sill/types/api';

import { useToast } from '../../app/providers.js';
import { Pill, RelativeTime } from '../../components/atoms/index.js';
import { Collapsible } from '../../components/atoms/Collapsible.js';
import type { useC } from '../../lib/theme.js';

type ThemeC = ReturnType<typeof useC>;

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const KIND_LABEL: Readonly<Record<InstallView['kind'], string>> = {
  worker: 'Cloudflare Worker',
  plugin: 'WordPress plugin',
};

const STATUS_TONE: Readonly<Record<InstallKeyView['status'], 'lime' | 'neutral' | 'coral'>> = {
  active: 'lime',
  retired: 'neutral',
  revoked: 'coral',
};

type Pending =
  | { readonly kind: 'key'; readonly id: string }
  | { readonly kind: 'install'; readonly id: string }
  | null;

export interface InstallKeysSectionProps {
  C: ThemeC;
  siteId: SiteId;
  api: Pick<SiteAPI, 'listSiteInstalls' | 'revokeInstallKey' | 'revokeInstall'>;
}

export function InstallKeysSection({ C, siteId, api }: InstallKeysSectionProps): JSX.Element | null {
  const toast = useToast();
  const [data, setData] = useState<SiteInstallsResponse | null | undefined>(undefined);
  const [confirming, setConfirming] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.listSiteInstalls(siteId));
    } catch {
      // An API that predates this feature (404) or any failure hides the
      // section rather than showing a broken card.
      setData(null);
    }
  }, [api, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = useCallback(async (): Promise<void> => {
    if (confirming === null || busy) return;
    setBusy(true);
    try {
      if (confirming.kind === 'key') await api.revokeInstallKey(siteId, confirming.id);
      else await api.revokeInstall(siteId, confirming.id);
      toast({ title: confirming.kind === 'key' ? 'Key revoked' : 'Install revoked', tone: 'lime' });
      setConfirming(null);
      await load();
    } catch {
      toast({ title: 'Could not revoke', body: 'Please try again.', tone: 'coral' });
    } finally {
      setBusy(false);
    }
  }, [api, busy, confirming, load, siteId, toast]);

  if (data === undefined || data === null) return null;

  const kicker = { fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1, fontWeight: 600, marginBottom: 6 };
  const body = { fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 };
  const linkBtn = (danger: boolean) => ({
    background: 'transparent',
    border: `1px solid ${C.border}`,
    color: danger ? C.coralFg : C.muted,
    fontFamily: sansFont,
    fontSize: 11.5,
    borderRadius: 2,
    padding: '3px 8px',
    cursor: 'pointer',
  });

  const confirmBox = (text: string): JSX.Element => (
    <div className="p-3 mt-2" style={{ background: C.coralDim, border: '1px solid rgba(255,107,71,0.3)', borderRadius: 3 }} role="alertdialog">
      <div style={{ ...body, color: C.text, marginBottom: 8 }}>{text}</div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void revoke()}
          disabled={busy}
          className="flex items-center gap-2 px-3 py-2"
          style={{ background: C.coralFg, color: C.ink, fontFamily: sansFont, fontWeight: 600, fontSize: 12, borderRadius: 2, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? <RefreshCw size={12} style={{ animation: 'spin 1.4s linear infinite' }} /> : null}
          Revoke
        </button>
        <button
          type="button"
          onClick={() => setConfirming(null)}
          disabled={busy}
          className="px-3 py-2"
          style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.muted, fontFamily: sansFont, fontSize: 12, borderRadius: 2 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="install-keys">
      <div style={kicker} className="flex items-center gap-1">
        <KeyRound size={10} /> INSTALL KEYS
      </div>
      {!data.signing_available ? (
        <div style={body}>Signed reports are not available yet on this account.</div>
      ) : (
        <>
          {data.installs.length === 0 ? (
            <div style={body}>No keys yet. Deploy the Worker or download the plugin to create one.</div>
          ) : (
            <>
            <div style={{ ...body, marginBottom: 4 }} data-testid="install-keys-summary">
              {installsSummary(data.installs)}
            </div>
            <Collapsible variant="inline" label="Manage keys">
            <div style={{ ...body, marginBottom: 10 }}>
              Each Worker deploy or plugin download signs its reports to Sill with its own key. A new deploy or
              download adds a key and keeps the previous one working (two at most), so revoke the older key once that
              file is gone. Revoked installs still report, but their reports are no longer marked as coming from your
              install.
            </div>
            <div className="flex flex-col gap-3">
              {data.installs.map((inst) => (
                <div key={inst.install_id} className="p-3" style={{ border: `1px solid ${C.border}`, borderRadius: 3 }} data-testid={`install-${inst.install_id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div style={{ fontFamily: sansFont, fontSize: 12.5, fontWeight: 600, color: C.text }}>
                      {KIND_LABEL[inst.kind]}{' '}
                      {inst.revoked_at !== null ? <Pill tone="coral">revoked</Pill> : null}
                    </div>
                    <div className="flex items-center gap-2" style={{ ...body, fontSize: 11 }}>
                      {inst.last_report_at !== null ? (
                        <span>
                          Last report <RelativeTime iso={inst.last_report_at} />
                        </span>
                      ) : (
                        <span>No reports yet</span>
                      )}
                      {inst.revoked_at === null ? (
                        <button
                          type="button"
                          style={linkBtn(true)}
                          onClick={() => setConfirming({ kind: 'install', id: inst.install_id })}
                        >
                          Revoke install
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {confirming?.kind === 'install' && confirming.id === inst.install_id
                    ? confirmBox(`Revoke every key of this ${KIND_LABEL[inst.kind]}? Its reports keep arriving but are no longer marked as coming from your install. Redeploy or download again to start signing with a new key.`)
                    : null}
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontFamily: sansFont, fontSize: 11.5 }}>
                    <thead>
                      <tr>
                        {['Key', 'Status', 'Created', 'Last used', ''].map((h) => (
                          <th key={h} style={{ textAlign: 'left', fontFamily: monoFont, fontSize: 9, letterSpacing: 1, color: C.muted, padding: '4px 6px', borderBottom: `1px solid ${C.border}` }}>
                            {h.toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inst.keys.map((k) => (
                        <tr key={k.key_id} data-testid={`key-${k.key_id}`}>
                          <td style={{ padding: '4px 6px', fontFamily: monoFont, color: C.text }}>{k.fingerprint}</td>
                          <td style={{ padding: '4px 6px' }}>
                            <Pill tone={STATUS_TONE[k.status]}>{k.status}</Pill>
                          </td>
                          <td style={{ padding: '4px 6px', color: C.muted }}>
                            <RelativeTime iso={k.created_at} />
                          </td>
                          <td style={{ padding: '4px 6px', color: C.muted }}>
                            {k.last_used_at !== null ? <RelativeTime iso={k.last_used_at} /> : 'Never'}
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                            {k.status === 'active' ? (
                              <button type="button" style={linkBtn(true)} onClick={() => setConfirming({ kind: 'key', id: k.key_id })}>
                                Revoke
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {confirming?.kind === 'key' && inst.keys.some((k) => k.key_id === confirming.id)
                    ? confirmBox('Revoke this key? A Worker or plugin still using it keeps reporting, but its reports are no longer marked as coming from your install.')
                    : null}
                </div>
              ))}
            </div>
            </Collapsible>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * One-line collapsed summary, e.g. "Cloudflare Worker · 2 active keys ·
 * last report 10h ago". Revoked installs are counted, not itemised.
 */
function installsSummary(installs: readonly InstallView[]): JSX.Element {
  const live = installs.filter((i) => i.revoked_at === null);
  const revoked = installs.length - live.length;
  const parts: JSX.Element[] = live.map((inst) => {
    const active = inst.keys.filter((k) => k.status === 'active').length;
    return (
      <span key={inst.install_id}>
        {KIND_LABEL[inst.kind]} · {active} active {active === 1 ? 'key' : 'keys'} ·{' '}
        {inst.last_report_at !== null ? (
          <>
            last report <RelativeTime iso={inst.last_report_at} />
          </>
        ) : (
          'no reports yet'
        )}
      </span>
    );
  });
  return (
    <>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 ? '; ' : null}
          {p}
        </span>
      ))}
      {revoked > 0 ? <span>{parts.length > 0 ? '; ' : null}{revoked} revoked</span> : null}
    </>
  );
}
